import {ServiceCategory} from "../model/serviceCategory";
import {BookableService} from "../model/bookableService";
import {Product} from "../model/product";
import {DocumentClass} from "../model/documentClass";

export default class NameFinderUtils{

    /**
     * Tries to get the long name of a category based on a short name and an array of all service categories where it should be looked for the long name
     *
     * @param serviceCategories All service categories where it should be looked for a long name
     * @param serviceShortName Short name based on which the long name should be found
     * @return Returns the long name if it can be determined else the short name
     */
    static getCategoryLongName(serviceCategories: ServiceCategory[], serviceShortName: string): string {
        let serviceCategory: ServiceCategory | undefined = serviceCategories.find(category => category.name === serviceShortName);
        return serviceCategory ? serviceCategory.displayName : serviceShortName;
    }

    /**
     * Tries to get the long name of a service based on a short name and map of all bookable services mapped by their category where it should be looked for the long name
     *
     * @param bookableServicesByCategory Map of all bookable services mapped by their category where it should be looked for the long name
     * @param serviceShortName Short name based on which the long name should be found
     * @return Returns the long name if it can be determined else the short name
     */
    static getServiceLongName(bookableServicesByCategory: Map<string, BookableService[]>, serviceShortName): string {
        for (const [, bookableServices] of bookableServicesByCategory.entries()) {
            const foundService = bookableServices.find(service => service.shortName === serviceShortName);
            if (foundService) {
                return foundService.longName;
            }
        }
        return serviceShortName;
    }

    /**
     * Tries to find the category name of a service based on the short name of the service
     *
     * @param shortName short name of the service
     * @param bookableServicesByCategory Map of all bookable services mapped by their category
     * @return Returns either the category name of the category the service is part
     * of or undefined if no service with the given short name can be found
     */
    static findBookableServiceCategoryByShortName(shortName: string, bookableServicesByCategory: Map<string, BookableService[]>): string | undefined{
        for (const [categoryName, bookableServices] of bookableServicesByCategory.entries()) {
            const foundService = bookableServices.find(service => service.shortName === shortName);
            if (foundService) {
                return categoryName;
            }
        }
        return undefined;
    }

    /**
     * Tries to find the category name of a product based on the short name of the product
     *
     * @param productShortName short name of the product
     * @param productsByCategory Map of all products mapped by their categories
     * @return Returns either the category name of the category the product is part of or undefined if no product with the given short name can be found
     */
    static findProductCategoryByShortName(productShortName: string, productsByCategory: Map<string, Product[]>): string | undefined {
        for (const [categoryName, products] of Object.entries(productsByCategory)) {
            const foundProduct = products.find(product => product.nameShort === productShortName);
            if (foundProduct) {
                return categoryName;
            }
        }
        return undefined;
    }

    /**
     * Determines the long name of a product or subclass based on a short name
     *
     * @param shortName shortName of a product or subClass
     * @param products List of all products where it should be looked for a long name
     * @param documentClass Document class in which subclasses it should be looked for a long name.
     * If no document class is given only the products are considered for looking for a long name
     * @return Returns the found long name if found else returns the short name.
     * If a long name is found the String "Auskunft" is removed from the name
     */
    static getProductLongName(shortName: string, products: Product[], documentClass: DocumentClass | null): string{
        let result: string;

        //Determine the long name based on a product short name
        products.forEach((product) => {
            if(product.nameShort.toUpperCase() === shortName.toUpperCase()){
                result = product.name_long
            }
            return;
        })

        //If no long name could be found based on a product short name and a documentClass is given try to find the
        //long name based on subclass short names from that document class
        if(!result && documentClass){
            documentClass.subClasses.forEach(subClass => {
                if(subClass.className.toUpperCase() === shortName.toUpperCase()){
                    result = subClass.name
                }
                return;
            })
        }

        return result ? result.replace("Auskunft", "") : shortName;
    }

}
